import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Collapse from '@mui/material/Collapse';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { CHECK_LABELS, resultColor, resultLabel } from './uavsMeta';
import { useTokens } from '../primitives';

function ResultIcon({ result }) {
  const t = useTokens();
  const sx = { fontSize: 18, color: resultColor(result, t) };
  if (result === 'pass') return <CheckCircleIcon sx={sx} />;
  if (result === 'warn') return <WarningIcon sx={sx} />;
  if (result === 'fail') return <CancelIcon sx={sx} />;
  return <RemoveCircleOutlineIcon sx={sx} />;
}

// Filter out null/undefined/empty values — the persisted evidence often
// includes keys like `cls: null` when a metric couldn't be measured, and
// showing those reads to a customer as if something is broken.
function meaningfulEntries(evidence) {
  if (!evidence) return [];
  return Object.entries(evidence).filter(([, v]) => {
    if (v == null) return false;
    if (typeof v === 'string' && v.trim() === '') return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  });
}

function EvidenceBlock({ entries }) {
  return (
    <Stack spacing={0.5}>
      {entries.map(([k, v]) => (
        <Box key={k} sx={{ display: 'flex', gap: 1, fontSize: 12 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              minWidth: 140,
              fontFamily: 'monospace',
            }}
          >
            {k}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.primary',
              wordBreak: 'break-word',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
            }}
          >
            {typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

export default function CheckRow({ check, evidence }) {
  const t = useTokens();
  const [showEvidence, setShowEvidence] = useState(false);
  const lost = (check.pointsMax || 0) - (check.pointsEarned || 0);
  const evidenceEntries = meaningfulEntries(evidence?.evidence);
  const hasEvidence = evidenceEntries.length > 0;

  return (
    <Box sx={{ py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Box sx={{ pt: 0.25 }}>
          <ResultIcon result={check.result} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {CHECK_LABELS[check.checkKey] || check.checkKey}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              {check.pointsEarned ?? 0}/{check.pointsMax ?? 0}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            component="div"
            sx={{ color: resultColor(check.result, t), fontWeight: 500 }}
          >
            {resultLabel(check.result)}
            {lost > 0 &&
              check.result !== 'skip' &&
              check.result !== 'error' && (
                <Box
                  component="span"
                  sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.5 }}
                >
                  · {lost} {lost === 1 ? 'point' : 'points'} to earn
                </Box>
              )}
          </Typography>
          {check.details && (
            <Typography
              variant="body2"
              sx={{ mt: 0.75, color: 'text.primary' }}
            >
              {check.details}
            </Typography>
          )}
          {check.recommendation && check.result !== 'pass' && (
            <Box
              sx={{
                mt: 0.75,
                pl: 1.25,
                borderLeft: '3px solid',
                borderColor: resultColor(check.result, t),
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                <strong>What to do:</strong> {check.recommendation}
              </Typography>
            </Box>
          )}
          {hasEvidence && (
            <Box sx={{ mt: 1 }}>
              <Link
                component="button"
                type="button"
                onClick={() => setShowEvidence((v) => !v)}
                underline="hover"
                sx={{
                  fontSize: 11,
                  color: 'text.secondary',
                  textTransform: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {showEvidence ? 'Hide' : 'Show'} technical details
              </Link>
              <Collapse in={showEvidence} unmountOnExit>
                <Box
                  sx={{
                    mt: 0.75,
                    p: 1.25,
                    bgcolor: t.subtle,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <EvidenceBlock entries={evidenceEntries} />
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
